import { Package, Server, Plug, ExternalLink, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface BoBDetailModalProps {
  bob: {
    id: string;
    name: string;
    version: string;
    description: string;
    category: string;
    author: string;
    size_compressed: number;
    metadata?: {
      requires_connector?: string;
      requires_odoo?: string;
      changelog_url?: string;
      dependencies?: string[];
      license?: string;
    };
  };
  isOpen: boolean;
  onClose: () => void;
  onInstall: () => void;
}

// Category color mapping for icon backgrounds
const categoryColors: Record<string, string> = {
  finance: "from-green-500/20 to-emerald-500/20",
  hr: "from-blue-500/20 to-sky-500/20",
  sales: "from-purple-500/20 to-violet-500/20",
  inventory: "from-orange-500/20 to-amber-500/20",
  productivity: "from-teal-500/20 to-cyan-500/20",
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export function BoBDetailModal({
  bob,
  isOpen,
  onClose,
  onInstall,
}: BoBDetailModalProps) {
  const categoryColor =
    categoryColors[bob.category] || "from-slate-500/20 to-slate-600/20";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="bob-detail-description"
        closeLabel="Close BoB details dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div
              className={cn(
                "h-12 w-12 rounded-lg bg-gradient-to-br flex items-center justify-center",
                categoryColor
              )}
              aria-hidden="true"
            >
              <Package className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <div className="text-slate-100">{bob.name}</div>
              <div className="text-sm font-normal text-slate-300">
                Version {bob.version}
              </div>
            </div>
          </DialogTitle>
          <DialogDescription id="bob-detail-description">
            {bob.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Metadata */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-slate-200">
              Package Information
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-300">Author</dt>
                <dd className="font-medium text-slate-200">{bob.author}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-300">Category</dt>
                <dd className="font-medium text-slate-200 capitalize">
                  {bob.category}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-300">Size</dt>
                <dd className="font-medium text-slate-200">
                  {formatBytes(bob.size_compressed)}
                </dd>
              </div>
              {bob.metadata?.license && (
                <div className="flex justify-between">
                  <dt className="text-slate-300">License</dt>
                  <dd className="font-medium text-slate-200">
                    {bob.metadata.license}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Requirements */}
          {(bob.metadata?.requires_odoo ||
            bob.metadata?.requires_connector) && (
            <div>
              <h3 className="text-sm font-semibold mb-3 text-slate-200">
                Requirements
              </h3>
              <div className="space-y-2 text-sm">
                {bob.metadata.requires_odoo && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Server className="h-4 w-4 text-slate-300" aria-hidden="true" />
                    <span>Odoo {bob.metadata.requires_odoo}</span>
                  </div>
                )}
                {bob.metadata.requires_connector && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Plug className="h-4 w-4 text-slate-300" aria-hidden="true" />
                    <span>Connector {bob.metadata.requires_connector}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dependencies */}
          {bob.metadata?.dependencies &&
            bob.metadata.dependencies.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3 text-slate-200">
                  Python Dependencies
                </h3>
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                  <code className="text-xs text-slate-300 font-mono block whitespace-pre-wrap">
                    {bob.metadata.dependencies.join("\n")}
                  </code>
                </div>
              </div>
            )}

          {/* Changelog link */}
          {bob.metadata?.changelog_url && (
            <div>
              <a
                href={bob.metadata.changelog_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-2 transition-colors"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                View Changelog
                <span className="sr-only">(opens in new tab)</span>
              </a>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onInstall}>
            <Download className="h-4 w-4 mr-2" aria-hidden="true" />
            Install BoB
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
